/**
 * The week ahead, this week and next: who joins and who leaves, the question HR and the
 * departments that prepare ask on a Monday, answered from the work itself
 * (an onboarding's start date, an offboarding's last day), so it cannot
 * disagree with the boards. The same lists are mailed to IT and Facilities
 * on the last working day before (decisions.md).
 */

import { shortDay, type Mover, type SiteWeek, type WeekPlan } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import { EmptyState, Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";

export function Week() {
  const { api } = useOrg();
  const { data, error } = useLoad(() => api.week(), [api]);
  if (!data && !error) return <Loading title="Week ahead" />;
  const sites = data?.sites ?? [];

  return (
    <section className="page">
      <PageHeader
        title="Week ahead"
        meta="Who joins and who leaves, this week and next, from each person's start date and last day."
      />
      <Problem>{error}</Problem>
      {data && sites.length === 0 ? (
        <EmptyState icon="calendar" title="No sites yet">
          The week ahead appears here once a site is set up in Settings.
        </EmptyState>
      ) : null}
      {sites.map((site) => (
        <Site key={site.siteId} site={site} several={sites.length > 1} />
      ))}
    </section>
  );
}

function Site({ site, several }: { site: SiteWeek; several: boolean }) {
  return (
    <>
      {several ? <h2 className="section-title">{site.siteName}</h2> : null}
      <Weekly title="This week" plan={site.thisWeek} today={site.today} />
      <Weekly title="Next week" plan={site.nextWeek} today={site.today} />
    </>
  );
}

function Weekly({ title, plan, today }: { title: string; plan: WeekPlan; today: string }) {
  const nobody = plan.starting.length + plan.leaving.length === 0;
  return (
    <div className="panel">
      <h3 className="panel-title">
        {title}{" "}
        <span className="muted">
          {shortDay(plan.start)} to {shortDay(plan.end)}
        </span>
      </h3>
      {nobody ? <p className="muted">Nobody is starting or leaving.</p> : null}
      <Movers heading="Starting" movers={plan.starting} kind="onboarding" today={today} />
      <Movers heading="Leaving" movers={plan.leaving} kind="offboarding" today={today} />
    </div>
  );
}

function Movers({
  heading,
  movers,
  kind,
  today,
}: {
  heading: string;
  movers: Mover[];
  kind: "onboarding" | "offboarding";
  today: string;
}) {
  if (movers.length === 0) return null;
  return (
    <div className="section">
      <h4 className="section-title">
        {heading} · {movers.length}
      </h4>
      <ul className="cards">
        {movers.map((mover) => (
          <li key={mover.instanceId}>
            <Link to={boardOf(mover.instanceId, kind)} className="card card-link">
              <div className="card-top">
                <p className="card-title">{mover.name}</p>
                <span className={`chip${mover.date === today ? " chip-accent" : ""}`}>
                  {mover.date === today ? "Today" : shortDay(mover.date)}
                </span>
              </div>
              <p className="card-meta">
                {mover.department ? <span className="chip">{mover.department}</span> : null}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
